import { useCallback, useEffect, useRef, useState } from 'react';

export const ESPERA_AL_TIPEAR_MS = 400;

export type EstadoDelPedido<T> =
  | { estado: 'QUIETO' }
  | { estado: 'CARGANDO'; anterior: T | null }
  | { estado: 'LISTO'; data: T }
  | { estado: 'ERROR'; error: unknown };

/**
 * Pide al backend cuando `clave` deja de cambiar por `espera` ms, y descarta
 * cualquier respuesta que no sea la del último pedido.
 *
 * - `/simular` pasa por el limitador de escrituras (120 por minuto): pedir en
 *   cada tecla lo agota. Por eso la espera es de 400 ms como mínimo.
 * - `clave` es un texto (p. ej. `JSON.stringify(cuerpo)`), no un objeto: un
 *   objeto nuevo en cada render dispararía un pedido en cada render.
 * - `clave === null` = no pedir (falta algo para poder contar).
 */
export function usePedidoConEspera<T>(
  clave: string | null,
  pedir: () => Promise<T>,
  espera: number = ESPERA_AL_TIPEAR_MS
): EstadoDelPedido<T> & { reintentar: () => void } {
  const [estado, setEstado] = useState<EstadoDelPedido<T>>({ estado: 'QUIETO' });
  const [vuelta, setVuelta] = useState(0);
  const pedirRef = useRef(pedir);
  pedirRef.current = pedir;
  const ultimo = useRef(0);
  const ultimoDato = useRef<T | null>(null);

  useEffect(() => {
    if (clave === null) {
      setEstado({ estado: 'QUIETO' });
      return;
    }
    const mio = ++ultimo.current;
    setEstado({ estado: 'CARGANDO', anterior: ultimoDato.current });
    const t = setTimeout(() => {
      // Promise.resolve().then: un `pedir` que tira sin devolver promesa cae en el catch.
      Promise.resolve()
        .then(() => pedirRef.current())
        .then((data) => {
          if (mio !== ultimo.current) return;
          ultimoDato.current = data;
          setEstado({ estado: 'LISTO', data });
        })
        .catch((error: unknown) => {
          if (mio !== ultimo.current) return;
          setEstado({ estado: 'ERROR', error });
        });
    }, espera);
    // Al cambiar la clave (también a null) o al desmontar, este pedido deja de
    // ser el último: si ya salió, su respuesta se descarta y no pisa QUIETO.
    // (`contador` es el mismo objeto ref: se lee su valor actual a propósito.)
    const contador = ultimo;
    return () => {
      clearTimeout(t);
      contador.current++;
    };
  }, [clave, espera, vuelta]);

  const reintentar = useCallback(() => setVuelta((v) => v + 1), []);
  return { ...estado, reintentar };
}
